import { ref } from 'vue'
import apiClient from '../api/axios.js'
import { useMotionParamSync } from './useMotionParamSync.js'

// 单舵机管理：servos 状态、片段增删、删除确认、单舵机调试下发、片段参数联动 watch
// 外部依赖（均为 Ref，除函数外）：
//   dualServos              - 双舵机数组（addMotion/removeMotion/removeServo 的双舵机分支使用）
//   showDualServoArm        - 双舵机机械臂视图开关（删除双舵机组时关闭）
//   selectedTrajectoryConfig- 当前选中的轨迹配置（删除双舵机组时清空）
//   selectedEnv / deviceId  - 调试指令下发目标
//   totalDuration           - 总时长（getServoTotalTime 内回写）
//   isRestoring             - 还原中标记（联动 watch 守卫）
//   showSnackbar            - 显示提示信息的回调 (msg: string) => void
export function useServoManager(deps) {
  const {
    dualServos, showDualServoArm, selectedTrajectoryConfig,
    selectedEnv, deviceId, totalDuration, isRestoring,
    showSnackbar
  } = deps

  const servos = ref([])
  const sevroDebug = ref(false) // 单舵机调试模式开关

  // 删除舵机确认
  const deleteServoDialog = ref(false)
  const deleteServoIndex = ref(null)

  // 片段参数联动（结束角->下段开始角、开始角->上段结束角、start/end 变化重置 1/3、2/3 点）
  useMotionParamSync(servos, { isRestoring, syncStartToPrevEnd: true })

  // 补全旧数据缺失的 param（不足 4 位时按直线插值补全）
  function fixServoParam(servoList) {
    servoList.forEach(servo => {
      servo.m.forEach(motion => {
        if (!motion.param) motion.param = [0, 0, 0, 0]
        if (motion.param.length < 4) {
          const start = motion.param[0] || 0
          const end = motion.param[1] || 0
          motion.param[2] = Number((start + (end - start) / 3).toFixed(1))
          motion.param[3] = Number((start + 2 * (end - start) / 3).toFixed(1))
        }
      })
    })
  }

  // 判断全局索引是否指向双舵机（索引超出单舵机数组长度）
  function isDualServo(index) {
    return index >= servos.value.length
  }

  function removeServo(index) {
    if (isDualServo(index)) {
      // 双舵机：直接删除整个配置
      dualServos.value = []
      showDualServoArm.value = false
      selectedTrajectoryConfig.value = null
    } else {
      // 单舵机：显示确认对话框
      deleteServoIndex.value = index
      deleteServoDialog.value = true
    }
  }

  function confirmDeleteServo() {
    if (deleteServoIndex.value !== null) {
      servos.value.splice(deleteServoIndex.value, 1)
      deleteServoIndex.value = null
    }
    deleteServoDialog.value = false
  }

  function cancelDeleteServo() {
    deleteServoDialog.value = false
    deleteServoIndex.value = null
  }

  // 添加片段：双舵机成对添加，单舵机只加自己；mIdx 非空时在其后插入
  function addMotion(sIdx, mIdx = null) {
    const insertMotion = (mArr) => {
      const insertIndex = mIdx !== null ? mIdx + 1 : mArr.length
      let startAngle = 0
      if (mIdx !== null && mIdx >= 0 && mIdx < mArr.length) {
        // 在指定位置插入，使用当前片段的结束角度作为新片段的开始角度
        startAngle = Number((Number(mArr[mIdx].param[1]) || 0).toFixed(1))
      } else {
        // 在最后添加，使用最后一个片段的结束角度
        startAngle = mArr.length > 0 ? Number((Number(mArr[mArr.length - 1].param[1]) || 0).toFixed(1)) : 0
      }
      mArr.splice(insertIndex, 0, {steps: 10, param: [startAngle, startAngle, startAngle, startAngle]})
    }

    if (isDualServo(sIdx)) {
      const dualIdx = sIdx - servos.value.length
      if (dualIdx >= 0 && dualServos.value[dualIdx]) {
        insertMotion(dualServos.value[dualIdx].m)
        // 成对添加：另一个舵机上也添加片段
        if (dualServos.value.length === 2) {
          const otherIdx = dualIdx === 0 ? 1 : 0
          insertMotion(dualServos.value[otherIdx].m)
        }
      }
    } else {
      insertMotion(servos.value[sIdx].m)
    }
  }

  // 删除片段：双舵机成对删除，单舵机只删自己
  function removeMotion(sIdx, mIdx) {
    if (isDualServo(sIdx)) {
      const dualIdx = sIdx - servos.value.length
      if (dualIdx >= 0 && dualServos.value[dualIdx]) {
        dualServos.value[dualIdx].m.splice(mIdx, 1)
        if (dualServos.value.length === 2 && dualServos.value[dualIdx].m.length > mIdx) {
          const otherIdx = dualIdx === 0 ? 1 : 0
          const otherMArr = dualServos.value[otherIdx].m
          if (otherMArr.length > mIdx) {
            otherMArr.splice(mIdx, 1)
          }
        }
      }
    } else {
      servos.value[sIdx].m.splice(mIdx, 1)
    }
  }

  // 计算舵机运行总时长（ms），并回写总步数到 totalDuration
  function getServoTotalTime(servo) {
    const totalSteps = servo.m.reduce((sum, motion) => sum + (motion.steps || 0), 0)
    const totalTime = servo.l * totalSteps * 20
    totalDuration.value = totalSteps
    return totalTime
  }

  // 单舵机调试：保存 Origin/Min/Max/Def、绝对/相对角度调试下发
  function handleAngleChange(servo, type, value) {
    showSnackbar(`舵机${servo.name}（${type}）调整为 ${value}°`)
    if (!selectedEnv.value.manager_url || !deviceId.value) return

    // Origin/Min/Max/Def 为保存标定位（operate=4 + data 编码），Debug 为绝对角度，DebugRelative 为相对角度
    const saveTypeData = { Min: 1, Origin: 2, Max: 3, Def: 4 }
    let operate, data
    if (type in saveTypeData) {
      operate = 4
      data = saveTypeData[type]
    } else if (type === 'Debug') {
      operate = 1
      data = value
    } else if (type === 'DebugRelative') {
      operate = 2
      data = value
    } else {
      return
    }

    const sendDebug = (operate, data) => apiClient.post(
      selectedEnv.value.manager_url + '/xiaozhi/device/send-message',
      {
        device_id: deviceId.value,
        params: {
          name: 'self.servo.debug',
          arguments: { operate, node: servo.s, data }
        }
      },
      { headers: { 'Content-Type': 'application/json' } }
    ).catch(() => {})

    sendDebug(operate, data)

    // 保存标定位时，额外下发当前角度值（operate=5）
    if (type in saveTypeData) {
      sendDebug(5, value)
    }
  }

  return {
    servos,
    sevroDebug,
    deleteServoDialog,
    fixServoParam,
    isDualServo,
    removeServo,
    confirmDeleteServo,
    cancelDeleteServo,
    addMotion,
    removeMotion,
    getServoTotalTime,
    handleAngleChange
  }
}
